import type { ChangeEvent } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { LoadingView } from "app/pages/sales-intelligence/modules/ai-agent/components/LoadingView/LoadingView";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { AIAgentEmailBodyEditor } from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentEmailBodyEditor/AIAgentEmailBodyEditor";
import LoaderGradient, {
    LOADER_GREY_GRADIENT_ID,
} from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Loader/LoaderGradient";

import { StyledInputContainer, StyledInput, StyledInputLoader } from "./styles";

type AIAgentEmailPreviewProps = {
    isInitialLoading: boolean;
    isLoading: boolean;
    isUpdatingVersion: boolean;
    subject: string;
    body: string;
    recipient: Contact | null;
    chartAnchorIndex: number;
    onSubjectChange: (v: string) => void;
    onBodyChange: (v: string, imageIndex: number) => void;
    hasPayload: boolean;
    selectedCompany: AiAgentEmailAutocompleteCompany | null;
    threadId: string;
    messageId: string;
    graphImg: string;
    isCompareChart?: boolean;
};

export const AIAgentEmailPreview = ({
    isInitialLoading,
    isLoading,
    isUpdatingVersion,
    subject,
    body,
    recipient,
    chartAnchorIndex = -1,
    onSubjectChange,
    onBodyChange,
    hasPayload,
    selectedCompany,
    threadId,
    messageId,
    graphImg,
    isCompareChart,
}: AIAgentEmailPreviewProps) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        onSubjectChange(value);
        AIAgentTrackingService.email.trackEmailSubjectEdit();
    };
    const renderContent = () => {
        if (!hasPayload) {
            return (
                <>
                    <SWReactIcons iconName="empty-state-browser" />
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[400]}
                        padding="8px"
                    >
                        {translate("si.pages.ai_agent_email.empty_state")}
                    </Text>
                </>
            );
        }

        if (isInitialLoading) {
            return (
                <Box width="100%">
                    <LoadingView showText />
                </Box>
            );
        }

        return (
            <>
                <Box
                    width="100%"
                    margin="0 0 12px"
                    data-automation="email-subject"
                >
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon[300]}
                        display="block"
                        margin="0 0 4px"
                    >
                        {translate("si.pages.ai_agent_email.subject")}
                    </Text>
                    {isUpdatingVersion ? (
                        <StyledInputLoader>
                            <LoaderGradient
                                height={14}
                                width={412}
                                id={LOADER_GREY_GRADIENT_ID}
                            />
                        </StyledInputLoader>
                    ) : (
                        <StyledInputContainer>
                            <StyledInput
                                type="text"
                                value={subject}
                                onChange={handleChange}
                            />
                        </StyledInputContainer>
                    )}
                </Box>

                <Stack
                    width="100%"
                    flex="1"
                    maxHeight="100%"
                    overflow="hidden"
                    justifyContent="space-between"
                    data-automation="email-body"
                >
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon[300]}
                        display="block"
                        margin="0 0 4px"
                    >
                        {translate("si.pages.ai_agent_email.body")}
                    </Text>
                    <Flex
                        maxHeight="calc(100% - 30px)"
                        flex="1"
                    >
                        <AIAgentEmailBodyEditor
                            subject={subject}
                            recipient={recipient}
                            value={body}
                            onTextChange={onBodyChange}
                            imgSrc={graphImg}
                            isCompareChart={isCompareChart}
                            imageIndex={chartAnchorIndex}
                            isLoading={isLoading}
                            isInitialLoading={isInitialLoading}
                            isUpdatingVersion={isUpdatingVersion}
                            threadId={threadId}
                            messageId={messageId}
                            selectedCompany={selectedCompany}
                        />
                    </Flex>
                </Stack>
            </>
        );
    };

    return (
        <Stack
            width="100%"
            flex="1"
            maxHeight="100%"
            overflow="hidden"
            data-automation="email-preview"
        >
            <Box
                width="100%"
                padding="16px"
                borderBottom={`1px solid ${colorsPalettes.carbon[50]}`}
                borderRadius="6px 6px 0 0"
                background={colorsPalettes.carbon[0]}
                data-automation="email-preview-title"
            >
                <Text
                    fontVariant="h6"
                    color={colorsPalettes.carbon[400]}
                >
                    {translate("si.pages.ai_agent_email.header")}
                </Text>
            </Box>

            <Stack
                gap="10px"
                flex="1"
                alignContent="center"
                justifyContent="center"
                alignItems="center"
                width="100%"
                maxHeight="100%"
                overflow="hidden"
                padding="16px 16px"
                borderRadius="0 0 6px 6px"
                background={colorsPalettes.carbon[0]}
            >
                {renderContent()}
            </Stack>
        </Stack>
    );
};
